import React from 'react'
import { Platform } from 'react-native'

import { useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import AddedToCartIcon from 'app/assets/icons/listing/added-to-cart.svg'
import Button from 'app/components/base/Button'
import useGuestAuth from 'app/hooks/useGuestAuth'
import Alert from 'app/services/Alert'
import ErrorHandler, { isErrorHandlerType } from 'app/services/ErrorHandler'
import { useAddListingToCartMutation } from 'app/services/Query/listing/listing.mutation'
import useAppStore from 'app/store/app.store'
import useAuthStore, { getIsAuthLoading } from 'app/store/auth.store'
import { palette } from 'app/theme/common/palette'

interface AddToCartButtonProps {
  slug: string
  disabled: boolean
  availableQuantitySum?: number
  sendGAEvent?: (quantity: string | number) => void
}

const AddToCartButton: RFC<AddToCartButtonProps> = ({
  slug,
  disabled,
  availableQuantitySum,
  sendGAEvent
}) => {
  const intl = useIntl()
  const isAuthLoading = useAuthStore(getIsAuthLoading)
  const isTablet = useAppStore((state) => state.isTablet)
  const methods = useFormContext<AddCartListingFV>()

  const { getGuestUser } = useGuestAuth()

  const { mutateAsync, isLoading, isSuccess } = useAddListingToCartMutation()

  const onSubmit = async (values: AddCartListingFV) => {
    try {
      await getGuestUser()

      setTimeout(async () => {
        await mutateAsync({
          listingSlug: slug,
          itemQuantity: values.item_quantity
        })
      }, 0)

      if (sendGAEvent) {
        sendGAEvent(values.item_quantity)
      }

      Alert.success(intl.formatMessage({ defaultMessage: 'Added to Cart' }))
    } catch (error) {
      if (isErrorHandlerType(error)) new ErrorHandler(error?.data)
    }
  }

  const disabledButton = disabled || isLoading || isAuthLoading
  const addedLastItem = isSuccess && availableQuantitySum === 1

  return (
    <>
      {!addedLastItem && (
        <Button
          onClick={methods.handleSubmit(onSubmit)}
          sx={{
            flex: 1,
            maxWidth: { xs: 'auto', md: isTablet ? 300 : 165 }
          }}
          disabled={disabledButton}
        >
          <FormattedMessage defaultMessage="Add to Cart" />
        </Button>
      )}

      {addedLastItem && (
        <Button
          disabled
          sx={{
            flex: 1,
            maxWidth: { xs: 'auto', md: 185 },
            ...Platform.select({
              web: {
                '&.Mui-disabled': {
                  backgroundColor: `${palette.secondary.main} !important`,
                  color: 'primary.main'
                }
              },
              native: {
                backgroundColor: 'secondary.main'
              }
            })
          }}
          {...Platform.select({
            web: {
              startIcon: <AddedToCartIcon />
            },
            native: {
              icon: () => <AddedToCartIcon />,
              labelSx: {
                color: 'text.primary'
              }
            }
          })}
        >
          <FormattedMessage defaultMessage="Added to Cart" />
        </Button>
      )}
    </>
  )
}

export default AddToCartButton
